import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import StatusSelect from "@/components/StatusSelect";
import { useLiveRefresh } from "@/contexts/LiveRefresh";
import TaskDetailDialog from "@/components/TaskDetailDialog";
import TaskFormDialog from "@/components/TaskFormDialog";
import { toast } from "sonner";
import {
    AlertTriangle,
    ArrowRight,
    BadgeIndianRupee,
    CheckCircle2,
    Clock,
    ListChecks,
    Receipt,
    PauseCircle,
    Building2,
    Loader2,
} from "lucide-react";

function fmtDate(d) {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return d;
    }
}

function KpiCard({ label, value, sub, icon: Icon, tone = "primary", testId, onClick }) {
    const toneMap = {
        primary: "bg-primary/5 text-primary border-primary/15",
        amber: "bg-amber-50 text-amber-700 border-amber-200",
        red: "bg-red-50 text-red-700 border-red-200",
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        blue: "bg-blue-50 text-blue-700 border-blue-200",
        purple: "bg-purple-50 text-purple-700 border-purple-200",
    };
    return (
        <Card
            className={`p-5 sdc-card ${onClick ? "cursor-pointer transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md" : ""}`}
            data-testid={testId}
            onClick={onClick}
            role={onClick ? "button" : undefined}
        >
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                        {label}
                    </div>
                    <div className="mt-2 font-display text-4xl font-extrabold tracking-tight text-foreground">
                        {value}
                    </div>
                    {sub && (
                        <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
                    )}
                </div>
                <div className={`grid h-10 w-10 place-items-center rounded-lg border ${toneMap[tone]}`}>
                    <Icon className="h-5 w-5" />
                </div>
            </div>
        </Card>
    );
}

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detailTask, setDetailTask] = useState(null);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const navigate = useNavigate();

    const load = () => {
        api.get("/dashboard/stats")
            .then((r) => setStats(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);
    useLiveRefresh(load);

    const changeStatus = async (taskId, status) => {
        try {
            await api.patch(`/tasks/${taskId}`, { status });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    if (loading) {
        return (
            <div className="grid place-items-center py-32">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    const total = stats?.total ?? 0;
    const inProgress = stats?.by_status?.in_progress ?? 0;
    const completed = stats?.by_status?.completed ?? 0;
    const overdue = stats?.overdue ?? 0;
    const unbilled = stats?.unbilled_completed_count ?? 0;
    const reimb = stats?.reimbursable_pending_count ?? 0;
    const pendingClient = stats?.by_status?.pending_from_client ?? 0;
    const pendingDept = stats?.by_status?.pending_from_department ?? 0;
    const showAlerts = unbilled > 0 || reimb > 0 || overdue > 0;

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Overview
                    </div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                        Dashboard
                    </h1>
                </div>
                <Button onClick={() => navigate("/tasks")} data-testid="dashboard-open-tasks">
                    Open task manager
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
            </div>

            {/* Smart alerts */}
            {showAlerts && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="smart-alerts">
                    {unbilled > 0 && (
                        <Card className="border-amber-200 bg-amber-50/70 p-4">
                            <div className="flex items-start gap-3">
                                <Receipt className="mt-0.5 h-5 w-5 text-amber-700" />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-amber-900">
                                        {unbilled} completed task{unbilled > 1 ? "s" : ""} not yet billed
                                    </div>
                                    <p className="mt-0.5 text-xs text-amber-800">
                                        Don't lose revenue — raise the bill for completed engagements.
                                    </p>
                                    <button
                                        onClick={() => navigate("/tasks?filter=unbilled")}
                                        className="mt-2 text-xs font-semibold text-amber-900 underline-offset-2 hover:underline"
                                    >
                                        Review unbilled →
                                    </button>
                                </div>
                            </div>
                        </Card>
                    )}
                    {reimb > 0 && (
                        <Card className="border-primary/30 bg-primary/5 p-4">
                            <div className="flex items-start gap-3">
                                <BadgeIndianRupee className="mt-0.5 h-5 w-5 text-primary" />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-primary">
                                        {reimb} reimbursable expense{reimb > 1 ? "s" : ""} pending bill
                                    </div>
                                    <p className="mt-0.5 text-xs text-primary/80">
                                        Add these to the client invoice before sending it out.
                                    </p>
                                    <button
                                        onClick={() => navigate("/tasks?filter=reimbursable")}
                                        className="mt-2 text-xs font-semibold text-primary underline-offset-2 hover:underline"
                                    >
                                        Review reimbursables →
                                    </button>
                                </div>
                            </div>
                        </Card>
                    )}
                    {overdue > 0 && (
                        <Card className="border-red-200 bg-red-50/70 p-4">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="mt-0.5 h-5 w-5 text-red-700" />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-red-900">
                                        {overdue} task{overdue > 1 ? "s" : ""} past deadline
                                    </div>
                                    <p className="mt-0.5 text-xs text-red-800">
                                        Reschedule or escalate to avoid client friction.
                                    </p>
                                    <button
                                        onClick={() => navigate("/tasks?filter=overdue")}
                                        className="mt-2 text-xs font-semibold text-red-900 underline-offset-2 hover:underline"
                                    >
                                        Review overdue →
                                    </button>
                                </div>
                            </div>
                        </Card>
                    )}
                </div>
            )}

            {/* KPI grid */}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                <KpiCard
                    label="Total Tasks"
                    value={total}
                    sub="Across all clients"
                    icon={ListChecks}
                    tone="primary"
                    testId="kpi-total"
                    onClick={() => navigate("/tasks")}
                />
                <KpiCard
                    label="In Progress"
                    value={inProgress}
                    sub="Currently active"
                    icon={Clock}
                    tone="amber"
                    testId="kpi-in-progress"
                    onClick={() => navigate("/tasks?status=in_progress")}
                />
                <KpiCard
                    label="Completed"
                    value={completed}
                    sub="Done & dusted"
                    icon={CheckCircle2}
                    tone="emerald"
                    testId="kpi-completed"
                    onClick={() => navigate("/tasks?status=completed")}
                />
                <KpiCard
                    label="Overdue"
                    value={overdue}
                    sub="Past deadline"
                    icon={AlertTriangle}
                    tone="red"
                    testId="kpi-overdue"
                    onClick={() => navigate("/tasks?filter=overdue")}
                />
                <KpiCard
                    label="Pending · Client"
                    value={pendingClient}
                    sub="Awaiting client"
                    icon={PauseCircle}
                    tone="blue"
                    testId="kpi-pending-client"
                    onClick={() => navigate("/tasks?status=pending_from_client")}
                />
                <KpiCard
                    label="Pending · Dept"
                    value={pendingDept}
                    sub="Awaiting department"
                    icon={Building2}
                    tone="purple"
                    testId="kpi-pending-department"
                    onClick={() => navigate("/tasks?status=pending_from_department")}
                />
            </div>

            {/* Recent */}
            <Card className="sdc-card overflow-hidden">
                <div className="flex items-center justify-between border-b border-border px-5 py-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                            Latest
                        </div>
                        <h2 className="font-display text-xl font-extrabold tracking-tight text-foreground">
                            Recent tasks
                        </h2>
                    </div>
                    <Button variant="ghost" onClick={() => navigate("/tasks")}>
                        View all
                    </Button>
                </div>

                {(stats?.recent_tasks?.length ?? 0) === 0 ? (
                    <div className="px-5 py-12 text-center text-sm text-muted-foreground">
                        No tasks yet. Head to the Task manager to add your first engagement.
                    </div>
                ) : (
                    <ul className="divide-y divide-border">
                        {stats.recent_tasks.map((t) => (
                            <li
                                key={t.id}
                                className="flex cursor-pointer items-center gap-4 px-5 py-3 sdc-table-row"
                                data-testid={`recent-task-${t.id}`}
                                onClick={() => setDetailTask(t)}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="truncate text-sm font-semibold text-foreground">
                                        {t.client_name}
                                    </div>
                                    <div className="truncate text-xs text-muted-foreground">
                                        {t.work_type} · Due {fmtDate(t.deadline_date)}
                                    </div>
                                </div>
                                <StatusSelect
                                    value={t.status}
                                    onChange={(v) => changeStatus(t.id, v)}
                                />
                                <div className="hidden sm:flex items-center gap-2">
                                    {t.reimbursable_expense && (
                                        <span className="sdc-pill border-primary/20 bg-primary/5 text-primary">
                                            Reimb.
                                        </span>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            <TaskDetailDialog
                open={!!detailTask}
                onOpenChange={(o) => !o && setDetailTask(null)}
                task={detailTask}
                onChanged={load}
                onEdit={(t) => {
                    setDetailTask(null);
                    setEditing(t);
                    setShowForm(true);
                }}
            />

            <TaskFormDialog
                open={showForm}
                onOpenChange={setShowForm}
                task={editing}
                onSaved={load}
            />
        </div>
    );
}
